import React, { useRef, useState, useEffect } from 'react';
import { CaretDoubleRight, Lock } from 'phosphor-react';
import './SSlider.css';

interface SSliderProps {
  onUnlock?: () => void;
  isUnlocked?: boolean;
  isOpening?: boolean;
  isGateOpened?: boolean;
  disabled?: boolean;
}

const THUMB_W = 56; // must match CSS

const SSlider: React.FC<SSliderProps> = ({
  onUnlock,
  isUnlocked = false,
  isOpening = false,
  isGateOpened = false,
  disabled = false,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumbPx, setThumbPx] = useState(0);
  const [progress, setProgress] = useState(0);

  const usableWidth = () =>
    (trackRef.current?.getBoundingClientRect().width ?? 0) - THUMB_W - 8;

  const clamp = (px: number) => Math.max(0, Math.min(usableWidth(), px));

  const applyPosition = (clientX: number) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const px = clamp(clientX - rect.left - THUMB_W / 2);
    const pct = (px / usableWidth()) * 100;
    setThumbPx(px);
    setProgress(pct);
    if (pct >= 97) {
      setThumbPx(usableWidth());
      setProgress(100);
      onUnlock?.();
    }
  };

  const reset = () => { setThumbPx(0); setProgress(0); };

  // Attach non-passive touch listeners directly so preventDefault works
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onStart = (e: TouchEvent) => { if (!disabled && !isUnlocked) e.preventDefault(); };
    const onMove = (e: TouchEvent) => {
      if (disabled || isUnlocked) return;
      e.preventDefault();
      applyPosition(e.touches[0].clientX);
    };
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
    };
  });

  const onTouchEnd = () => { if (!isUnlocked) reset(); };

  // ── Mouse ──────────────────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    if (disabled || isUnlocked) return;
    e.preventDefault();
    const move = (ev: MouseEvent) => applyPosition(ev.clientX);
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      if (!isUnlocked) reset();
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  const active = isOpening || isGateOpened;
  const fillPct = active ? 100 : progress;
  const thumbLeft = active ? usableWidth() : thumbPx;

  return (
    <div
      ref={trackRef}
      className={`s-slider-track ${active ? 'opening' : ''}`}
      onTouchEnd={onTouchEnd}
    >
      {/* Fill */}
      <div className="s-slider-fill" style={{ width: `${fillPct}%` }} />

      {/* Label */}
      <span className={`s-slider-label ${active ? 's-slider-label--light' : ''}`}>
        <Lock size={15} color={active ? '#FFFFFF' : '#BD9734'} />
        {active ? 'Unlocking…' : 'Slide to Unlock'}
      </span>

      {/* Thumb */}
      <button
        type="button"
        className={`s-slider-thumb ${active ? 'opening' : ''}`}
        style={{ left: `${thumbLeft + 4}px` }}
        onMouseDown={onMouseDown}
        disabled={disabled || isUnlocked || isOpening}
        aria-label="Drag to unlock"
      >
        <CaretDoubleRight size={22} color="#FFFFFF" weight="bold" />
      </button>
    </div>
  );
};

export default SSlider;
